import { z } from 'zod';

import { jsonRequest } from './shared';
import { ApiResponseError } from './response';

export type ClockAction = 'clock_in' | 'clock_out' | 'break_start' | 'break_end' | 'override';
export type ClockSource = 'pos' | 'pwa' | 'kiosk' | 'api' | 'offline_sync';
export type TipType = 'cash' | 'charged' | 'service_charge';
export type TipPoolMethod = 'hours' | 'role_weight' | 'percentage';

export interface TipPool {
  id: string;
  tenantId: string;
  locationId: string | null;
  name: string;
  method: TipPoolMethod;
  config: Record<string, unknown>;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface TipPoolAssignment {
  id: string;
  tenantId: string;
  poolId: string;
  employeeId: string;
  weight: number;
  role: string | null;
  metadata: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

export interface TipDistributionEntry {
  id: string;
  runId: string | null;
  poolId: string;
  employeeId: string;
  employeeName: string | null | undefined;
  tipType: TipType;
  amountCents: number;
  sourceLedgerIds?: string[];
  metadata?: Record<string, unknown>;
}

export interface TipDistributionRunSummary {
  runId: string;
  poolId: string;
  poolName: string;
  method: TipPoolMethod;
  tipType: TipType;
  grossTipCents: number;
  distributedTipCents: number;
  remainderCents: number;
  status: string;
  remainderMetadata: Record<string, unknown>;
  configSnapshot: Record<string, unknown>;
  ledgerCount: number | null;
  assignmentCount: number;
  completedAt: string | null;
  distributions: TipDistributionEntry[];
}

export interface TipDistributionSummary {
  runs: TipDistributionRunSummary[];
  totals: {
    poolCount: number;
    runCount: number;
    grossTipCents: number;
    distributedTipCents: number;
    remainderCents: number;
  };
}
export type ExceptionStatus = 'open' | 'acknowledged' | 'resolved' | 'ignored';
export type ExceptionSeverity = 'info' | 'warning' | 'critical';
export type PayrollPeriodStatus = 'draft' | 'review' | 'locked';

export interface ClockEventPayload {
  employeeId: string;
  action: ClockAction;
  locationId?: string | null;
  source?: ClockSource;
  occurredAt: string | number | Date;
  deviceId?: string | null;
  deviceLabel?: string | null;
  offlineNonce?: string | null;
  ipAddress?: string | null;
  geo?: {
    latitude: number;
    longitude: number;
    accuracyMeters?: number;
  } | null;
  metadata?: Record<string, unknown>;
}

export interface ClockEventResponse {
  ok: boolean;
  event: Record<string, unknown>;
  shift: Record<string, unknown>;
  exceptions: Array<Record<string, unknown>>;
}

const ClockEventResponseSchema = z
  .object({
    ok: z.boolean(),
    event: z.record(z.unknown()),
    shift: z.record(z.unknown()),
    exceptions: z.array(z.record(z.unknown())),
  })
  .passthrough();

const ClockEventBatchResponseSchema = z
  .object({
    ok: z.boolean(),
    results: z.array(z.record(z.unknown())),
  })
  .passthrough();

const TimekeepingExceptionSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    employeeId: z.string().nullable(),
    locationId: z.string().nullable(),
    shiftId: z.string().nullable(),
    clockEventId: z.string().nullable(),
    type: z.string(),
    severity: z.string(),
    status: z.string(),
    summary: z.string(),
    details: z.string().nullable().optional(),
    raisedAt: z.string(),
    resolvedAt: z.string().nullable().optional(),
    resolvedBy: z.string().nullable().optional(),
    resolutionNote: z.string().nullable().optional(),
    metadata: z.record(z.unknown()),
  })
  .passthrough();

const TimekeepingExceptionListSchema = z
  .object({
    ok: z.boolean(),
    exceptions: z.array(TimekeepingExceptionSchema),
  })
  .passthrough();

export const recordClockEvent = async (payload: ClockEventPayload): Promise<ClockEventResponse> => {
  return jsonRequest<ClockEventResponse>('/api/time/clock-events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    schema: ClockEventResponseSchema,
  });
};

export const recordOfflineClockBatch = async (payload: ClockEventPayload[]): Promise<{ ok: boolean; results: ClockEventResponse['event'][] }> => {
  return jsonRequest<{ ok: boolean; results: ClockEventResponse['event'][] }>('/api/time/offline-batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    schema: ClockEventBatchResponseSchema,
  });
};

export interface TimekeepingException {
  id: string;
  tenantId: string;
  employeeId: string | null;
  locationId: string | null;
  shiftId: string | null;
  clockEventId: string | null;
  type: string;
  severity: ExceptionSeverity;
  status: ExceptionStatus;
  summary: string;
  details?: string | null;
  raisedAt: string;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolutionNote?: string | null;
  metadata: Record<string, unknown>;
}

export const fetchTimekeepingExceptions = async (opts: {
  status?: ExceptionStatus[];
  severity?: ExceptionSeverity[];
  locationId?: string | null;
  employeeId?: string | null;
} = {}): Promise<TimekeepingException[]> => {
  const params = new URLSearchParams();
  if (opts.status && opts.status.length > 0) {
    params.set('status', opts.status.join(','));
  }
  if (opts.severity && opts.severity.length > 0) {
    params.set('severity', opts.severity.join(','));
  }
  if (opts.locationId !== undefined) {
    params.set('locationId', opts.locationId ?? '');
  }
  if (opts.employeeId !== undefined) {
    params.set('employeeId', opts.employeeId ?? '');
  }

  const url = params.size > 0 ? `/api/time/exceptions?${params.toString()}` : '/api/time/exceptions';
  const payload = await jsonRequest<{ ok: boolean; exceptions: TimekeepingException[] }>(url, {
    schema: TimekeepingExceptionListSchema,
  });
  return payload.exceptions ?? [];
};

export const resolveTimekeepingException = async (
  exceptionId: string,
  payload: { status?: ExceptionStatus; resolutionNote?: string },
): Promise<TimekeepingException> => {
  const body = await jsonRequest<{ ok: boolean; exception: TimekeepingException }>(
    `/api/time/exceptions/${exceptionId}/resolve`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      schema: ResolveExceptionResponseSchema,
    },
  );
  return body.exception;
};

export interface TipDeclarationPayload {
  employeeId: string;
  shiftId?: string | null;
  locationId?: string | null;
  poolId?: string | null;
  assignmentStatus?: 'assigned' | 'unassigned';
  amountCents: number;
  tipType?: TipType;
  declaredAt?: string | Date;
  metadata?: Record<string, unknown>;
}

const TipResponseSchema = z
  .object({
    ok: z.boolean(),
    tip: z.record(z.unknown()),
  })
  .passthrough();

export const declareTip = async (payload: TipDeclarationPayload): Promise<Record<string, unknown>> => {
  const body = await jsonRequest<{ ok: boolean; tip: Record<string, unknown> }>('/api/time/tips', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: {
      employeeId: payload.employeeId,
      shiftId: payload.shiftId ?? null,
      locationId: payload.locationId ?? null,
      poolId: payload.poolId ?? null,
      assignmentStatus: payload.assignmentStatus,
      amountCents: payload.amountCents,
      tipType: payload.tipType,
      declaredAt: payload.declaredAt,
      metadata: payload.metadata,
    },
    schema: TipResponseSchema,
  });
  return body.tip;
};

export interface PayrollPeriodSummary {
  id: string;
  tenantId: string;
  locationId: string | null;
  startDate: string;
  endDate: string;
  status: PayrollPeriodStatus;
  generatedAt: string;
  lockedAt?: string | null;
  lockedBy?: string | null;
  totalHours: number;
  overtimeHours: number;
  tipCreditFlag: boolean;
  metadata: Record<string, unknown>;
}

const PayrollPeriodSummarySchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    locationId: z.string().nullable(),
    startDate: z.string(),
    endDate: z.string(),
    status: z.string(),
    generatedAt: z.string(),
    lockedAt: z.string().nullable().optional(),
    lockedBy: z.string().nullable().optional(),
    totalHours: z.number(),
    overtimeHours: z.number(),
    tipCreditFlag: z.boolean(),
    metadata: z.record(z.unknown()),
  })
  .passthrough();

export interface StagePayrollPeriodResult {
  period: PayrollPeriodSummary;
  tipPoolingEnabled: boolean;
}

export const stagePayrollPeriodRequest = async (payload: {
  locationId?: string | null;
  startDate: string | Date;
  endDate: string | Date;
}): Promise<StagePayrollPeriodResult> => {
  const body = await jsonRequest<{ ok: boolean; period: PayrollPeriodSummary; tipPooling?: { enabled: boolean } }>(
    '/api/time/pay-periods/stage',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      schema: StagePayrollPeriodResponseSchema,
    },
  );
  return {
    period: body.period,
    tipPoolingEnabled: body.tipPooling?.enabled ?? false,
  };
};

export const fetchPayrollPeriods = async (opts: {
  status?: PayrollPeriodStatus[];
  locationId?: string | null;
} = {}): Promise<PayrollPeriodSummary[]> => {
  const params = new URLSearchParams();
  if (opts.status && opts.status.length > 0) {
    params.set('status', opts.status.join(','));
  }
  if (opts.locationId !== undefined) {
    params.set('locationId', opts.locationId ?? '');
  }

  const url = params.size > 0 ? `/api/time/pay-periods?${params.toString()}` : '/api/time/pay-periods';
  const body = await jsonRequest<{ ok: boolean; periods: PayrollPeriodSummary[] }>(url, {
    schema: PayrollPeriodsResponseSchema,
  });
  return body.periods ?? [];
};

export interface CreateTipPoolPayload {
  name: string;
  method: TipPoolMethod;
  locationId?: string | null;
  config?: Record<string, unknown>;
  isActive?: boolean;
}

export interface UpdateTipPoolPayload {
  name?: string;
  method?: TipPoolMethod;
  locationId?: string | null;
  config?: Record<string, unknown>;
  isActive?: boolean;
}

export interface UpsertTipPoolAssignmentPayload {
  employeeId: string;
  weight?: number;
  role?: string | null;
  metadata?: Record<string, unknown>;
}

const isTipPoolingUnavailableError = (error: unknown): boolean =>
  error instanceof ApiResponseError && (error.status === 403 || error.status === 404);

export const listTipPools = async (): Promise<TipPool[]> => {
  try {
    const body = await jsonRequest<{ ok: boolean; pools: TipPool[] }>('/api/time/tip-pools', {
      schema: TipPoolListResponseSchema,
    });
    return body.pools ?? [];
  } catch (error) {
    if (isTipPoolingUnavailableError(error)) {
      return [];
    }
    throw error;
  }
};

export const createTipPool = async (payload: CreateTipPoolPayload): Promise<TipPool> => {
  const body = await jsonRequest<{ ok: boolean; pool: TipPool }>(
    '/api/time/tip-pools',
    {
      method: 'POST',
      body: payload,
      schema: TipPoolResponseSchema,
    },
  );
  return body.pool;
};

export const updateTipPool = async (poolId: string, payload: UpdateTipPoolPayload): Promise<TipPool> => {
  const body = await jsonRequest<{ ok: boolean; pool: TipPool }>(
    `/api/time/tip-pools/${poolId}`,
    {
      method: 'PATCH',
      body: payload,
      schema: TipPoolResponseSchema,
    },
  );
  return body.pool;
};

export const deleteTipPool = async (poolId: string): Promise<void> => {
  await jsonRequest<undefined>(`/api/time/tip-pools/${poolId}`, {
    method: 'DELETE',
    schema: z.undefined(),
  });
};

export const listTipPoolAssignments = async (poolId: string): Promise<TipPoolAssignment[]> => {
  try {
    const body = await jsonRequest<{ ok: boolean; assignments: TipPoolAssignment[] }>(
      `/api/time/tip-pools/${poolId}/assignments`,
      {
        schema: TipPoolAssignmentsResponseSchema,
      },
    );
    return body.assignments ?? [];
  } catch (error) {
    if (isTipPoolingUnavailableError(error)) {
      return [];
    }
    throw error;
  }
};

export const createTipPoolAssignment = async (
  poolId: string,
  payload: UpsertTipPoolAssignmentPayload,
): Promise<TipPoolAssignment> => {
  const body = await jsonRequest<{ ok: boolean; assignment: TipPoolAssignment }>(
    `/api/time/tip-pools/${poolId}/assignments`,
    {
      method: 'POST',
      body: payload,
      schema: z
        .object({
          ok: z.boolean(),
          assignment: TipPoolAssignmentSchema,
        })
        .passthrough(),
    },
  );
  return body.assignment;
};

export const updateTipPoolAssignment = async (
  poolId: string,
  assignmentId: string,
  payload: UpsertTipPoolAssignmentPayload,
): Promise<TipPoolAssignment> => {
  const body = await jsonRequest<{ ok: boolean; assignment: TipPoolAssignment }>(
    `/api/time/tip-pools/${poolId}/assignments/${assignmentId}`,
    {
      method: 'PATCH',
      body: payload,
      schema: z
        .object({
          ok: z.boolean(),
          assignment: TipPoolAssignmentSchema,
        })
        .passthrough(),
    },
  );
  return body.assignment;
};

export const deleteTipPoolAssignment = async (poolId: string, assignmentId: string): Promise<void> => {
  await jsonRequest<undefined>(`/api/time/tip-pools/${poolId}/assignments/${assignmentId}`, {
    method: 'DELETE',
    schema: z.undefined(),
  });
};

export const getTipDistributionSummary = async (periodId: string): Promise<TipDistributionSummary> => {
  const body = await jsonRequest<{ ok: boolean; summary: TipDistributionSummary }>(
    `/api/time/pay-periods/${periodId}/tip-distributions`,
    {
      schema: TipDistributionSummaryResponseSchema,
    },
  );
  return body.summary;
};

export const rerunTipDistribution = async (periodId: string): Promise<TipDistributionSummary> => {
  const body = await jsonRequest<{ ok: boolean; summary: TipDistributionSummary }>(
    `/api/time/pay-periods/${periodId}/distribute-tips`,
    {
      method: 'POST',
      schema: TipDistributionSummaryResponseSchema,
    },
  );
  return body.summary;
};

export interface PayrollPeriodEntry {
  id: string;
  employeeId: string;
  regularSeconds: number;
  overtimeSeconds: number;
  premiumSeconds: number;
  tipsCashCents: number;
  tipsChargedCents: number;
  tipsServiceCents: number;
  ptoSeconds: number;
  complianceFlags: string[];
  metadata: Record<string, unknown>;
}

const PayrollPeriodEntrySchema = z
  .object({
    id: z.string(),
    employeeId: z.string(),
    regularSeconds: z.number(),
    overtimeSeconds: z.number(),
    premiumSeconds: z.number(),
    tipsCashCents: z.number(),
    tipsChargedCents: z.number(),
    tipsServiceCents: z.number(),
    ptoSeconds: z.number(),
    complianceFlags: z.array(z.string()),
    metadata: z.record(z.unknown()),
  })
  .passthrough();

const StagePayrollPeriodResponseSchema = z
  .object({
    ok: z.boolean(),
    period: PayrollPeriodSummarySchema,
    tipPooling: z
      .object({
        enabled: z.boolean(),
      })
      .optional(),
  })
  .passthrough();

const PayrollPeriodsResponseSchema = z
  .object({
    ok: z.boolean(),
    periods: z.array(PayrollPeriodSummarySchema),
  })
  .passthrough();

const PayrollPeriodResponseSchema = z
  .object({
    ok: z.boolean(),
    period: PayrollPeriodSummarySchema,
    entries: z.array(PayrollPeriodEntrySchema),
  })
  .passthrough();

const AccrualBalanceSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    employeeId: z.string(),
    policyId: z.string(),
    balanceHours: z.number(),
    updatedAt: z.string(),
    metadata: z.record(z.unknown()),
  })
  .passthrough();

const TipPoolSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    locationId: z.string().nullable(),
    name: z.string(),
    method: z.string(),
    config: z.record(z.unknown()),
    isActive: z.boolean(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

const TipPoolAssignmentSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    poolId: z.string(),
    employeeId: z.string(),
    weight: z.number(),
    role: z.string().nullable(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

const TipDistributionEntrySchema = z
  .object({
    id: z.string(),
    runId: z.string().nullable(),
    poolId: z.string(),
    employeeId: z.string(),
    employeeName: z.string().nullable().optional(),
    tipType: z.string(),
    amountCents: z.number(),
    sourceLedgerIds: z.array(z.string()).optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .passthrough();

const TipDistributionRunSchema = z
  .object({
    runId: z.string(),
    poolId: z.string(),
    poolName: z.string(),
    method: z.string(),
    tipType: z.string(),
    grossTipCents: z.number(),
    distributedTipCents: z.number(),
    remainderCents: z.number(),
    status: z.string(),
    remainderMetadata: z.record(z.unknown()),
    configSnapshot: z.record(z.unknown()),
    ledgerCount: z.number().nullable(),
    assignmentCount: z.number(),
    completedAt: z.string().nullable(),
    distributions: z.array(TipDistributionEntrySchema),
  })
  .passthrough();

const TipDistributionSummarySchema = z
  .object({
    runs: z.array(TipDistributionRunSchema),
    totals: z.object({
      poolCount: z.number(),
      runCount: z.number(),
      grossTipCents: z.number(),
      distributedTipCents: z.number(),
      remainderCents: z.number(),
    }),
  })
  .passthrough();

const TipPoolListResponseSchema = z
  .object({
    ok: z.boolean(),
    pools: z.array(TipPoolSchema),
  })
  .passthrough();

const TipPoolResponseSchema = z
  .object({
    ok: z.boolean(),
    pool: TipPoolSchema,
  })
  .passthrough();

const TipPoolAssignmentsResponseSchema = z
  .object({
    ok: z.boolean(),
    assignments: z.array(TipPoolAssignmentSchema),
  })
  .passthrough();

const TipDistributionSummaryResponseSchema = z
  .object({
    ok: z.boolean(),
    summary: TipDistributionSummarySchema,
  })
  .passthrough();

const AccrualBalancesResponseSchema = z
  .object({
    ok: z.boolean(),
    balances: z.array(AccrualBalanceSchema),
  })
  .passthrough();

const ResolveExceptionResponseSchema = z
  .object({
    ok: z.boolean(),
    exception: TimekeepingExceptionSchema,
  })
  .passthrough();

export const fetchPayrollPeriod = async (
  periodId: string,
): Promise<{ period: PayrollPeriodSummary; entries: PayrollPeriodEntry[] }> => {
  const body = await jsonRequest<{ ok: boolean; period: PayrollPeriodSummary; entries: PayrollPeriodEntry[] }>(
    `/api/time/pay-periods/${periodId}`,
    {
      schema: PayrollPeriodResponseSchema,
    },
  ).catch((error: unknown) => {
    if (error instanceof ApiResponseError && error.status === 404) {
      throw new Error('Payroll period not found');
    }
    throw error;
  });
  return { period: body.period, entries: body.entries ?? [] };
};

export const lockPayrollPeriodRequest = async (periodId: string): Promise<PayrollPeriodSummary> => {
  const body = await jsonRequest<{ ok: boolean; period: PayrollPeriodSummary }>(
    `/api/time/pay-periods/${periodId}/lock`,
    {
      method: 'POST',
      schema: StagePayrollPeriodResponseSchema,
    },
  );
  return body.period;
};

export interface AccrualBalance {
  id: string;
  tenantId: string;
  employeeId: string;
  policyId: string;
  balanceHours: number;
  updatedAt: string;
  metadata: Record<string, unknown>;
}

export const fetchAccrualBalances = async (employeeId?: string): Promise<AccrualBalance[]> => {
  const params = new URLSearchParams();
  if (employeeId) {
    params.set('employeeId', employeeId);
  }
  const url = params.size > 0 ? `/api/time/accruals/balances?${params.toString()}` : '/api/time/accruals/balances';
  const body = await jsonRequest<{ ok: boolean; balances: AccrualBalance[] }>(url, {
    schema: AccrualBalancesResponseSchema,
  });
  return body.balances ?? [];
};
